import Box from '@mui/material/Box';

import { AppImage } from '@/components/atoms';
import { type DeliveryMedia } from '@/services/deliveries';

type Props = {
  media: DeliveryMedia;
  /** What width the tile is actually drawn at, so the right rendition is fetched. */
  sizes: string;
  priority?: boolean;
};

/**
 * The picture on a tile: a photograph, or a clip's poster frame.
 *
 * Deliberately not `MediaFrame`, which is the site's one photograph component
 * and the right one almost everywhere. `MediaFrame` draws a *local* `Picture`
 * from the build and falls back to a designed plate when the file has not been
 * supplied — both of which are wrong here. These files are remote (Storage,
 * once the archive is real), and a missing one is not a slot awaiting a
 * photograph but a record that should not have been returned, which the data
 * layer drops rather than drawing around.
 *
 * It also owns no ratio. Every other frame on the site crops to a shape the
 * layout chose; this one takes the shape of what it is given, because the grid
 * above it is masonry and a portrait handover photograph earns a taller tile
 * instead of being cropped to a letterbox.
 *
 * `alt=""` on purpose, and the one place on this page where that is right: the
 * tile is a button, the button carries the full description as its accessible
 * name, and an alt here would make a screen reader read the same sentence
 * twice before announcing the control.
 */
export function DeliveryTileMedia({ media, sizes, priority = false }: Props) {
  const src = media.kind === 'video' ? media.poster : media.src;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: 'surfaceVariant',
        overflow: 'hidden',
      }}
    >
      {src && (
        <AppImage
          src={src}
          alt=""
          width={media.width ?? 1200}
          height={media.height ?? 800}
          sizes={sizes}
          priority={priority}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            // Paired with the tile's own hover rule. Only `transform` moves,
            // so the lift stays on the compositor and cannot cause layout.
            transition: 'transform 420ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        />
      )}
    </Box>
  );
}
